import { FormState, useGetApiKeysListQuery, useDeleteApiKeyMutation, useCreateApiKeyMutation } from "@circuitry-ai/doc-data";
import { checkType, CtyButton, CtyShowToast, SimpleDeleteConfirmationModal, generateCircuitryLogoSvgBlobUrl, textCapitalize, ErrorBoundary, useAgGridStatePersistence, useHasApplicationResource } from "@circuitry-ai/doc-ui";
import { Icon } from "@iconify/react";
import { Image, Spinner, Tooltip } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import ApiKeyConfiguration from "./apikeyconfiguration";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, GridReadyEvent } from 'ag-grid-community';

// ErrorBoundary-wrapped components hoisted to module scope
const ApiKeyConfigurationWithErrorBoundary = ErrorBoundary(ApiKeyConfiguration);
const DeleteConfirmationModalWithErrorBoundary = ErrorBoundary(SimpleDeleteConfirmationModal as unknown as React.ComponentType<Record<string, unknown>>);

export interface ApiKeysGridContentProps {
    readonly apiKeysList: any[];
    readonly daysLeft: (targetDate: string) => number;
    readonly textCapitalize: (text: string) => string;
    readonly handleShowDeleteConfirmationModal: (item: any) => void;
    readonly handleViewAPIKey: (item: any, isApiKeyExpired?: boolean) => void;
    readonly t: (key: string) => string;
    readonly isCreateAPiKeyLoading: boolean;
    readonly currentItem: any;
    readonly formatDate: (dateStr: string) => string;
    readonly onGridReady?: (params: GridReadyEvent) => void;
    readonly isActionsDisabled?: boolean;
}

interface StatusCellRendererProps {
    readonly data: any;
    readonly daysLeft: (targetDate: string) => number;
    readonly t: (key: string) => string;
}

function StatusCellRenderer({ data, daysLeft, t }: StatusCellRendererProps) {
    const days = data ? daysLeft(data.expiration_date) : 0;
    const isRevoked = days <= 0;
    return (
        <span className={isRevoked ? 'text-danger' : 'text-success'}>
            {isRevoked ? t('Revoked') : t('Active')}
        </span>
    );
}

interface ApiKeyActionsCellProps {
    readonly item: any;
    readonly daysLeft: (targetDate: string) => number;
    readonly isCreateAPiKeyLoading: boolean;
    readonly currentItem: any;
    readonly onView: (item: any) => void;
    readonly onDelete: (item: any) => void;
    readonly onGenerate: (item: any) => void;
    readonly t: (key: string) => string;
    readonly isDisabled?: boolean;
}

function ApiKeyActionsCell({
    item,
    daysLeft,
    isCreateAPiKeyLoading,
    currentItem,
    onView,
    onDelete,
    onGenerate,
    t,
    isDisabled,
}: ApiKeyActionsCellProps) {
    if (!item) return null;
    const days = daysLeft(item.expiration_date);
    const isApiKeyExpired = days <= 0;
    const isThisGenerating = isCreateAPiKeyLoading && currentItem?.api_key_id === item.api_key_id;
    
    return (
        <div className="flex items-center justify-start gap-2 h-full py-1 flex-wrap">
            <Tooltip content={t('View')} placement="top">
                <button
                    type="button"
                    className="cursor-pointer text-primary inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={(e) => { e.stopPropagation(); onView(item); }}
                    aria-label={t('View')}
                    disabled={isDisabled}
                >
                    <Icon icon="solar:eye-bold" width="18" height="18" />
                </button>
            </Tooltip>
            <Tooltip content={t('Delete')} placement="top">
                <CtyButton
                    size="sm"
                    color="danger"
                    isIconOnly={true}
                    variant="light"
                    onPress={() => onDelete(item)}
                    className="min-w-0 px-2"
                    isDisabled={isDisabled}
                >
                    <Icon icon="hugeicons:delete-02" width="18" height="18" />
                </CtyButton>
            </Tooltip>
            {isApiKeyExpired ? (
                <CtyButton
                    size="sm"
                    color="primary"
                    isIconOnly={true}
                    variant="light"
                    isLoading={isThisGenerating}
                    onPress={() => onGenerate(item)}
                    className="min-w-0 px-2"
                    isDisabled={isDisabled}
                >
                    <Icon icon="teenyicons:plus-circle-outline" width="18" height="18" />
                </CtyButton>
            ) : (
                <Tooltip content={t('Copy')} placement="top">
                    <CtyButton
                        size="sm"
                        color="primary"
                        isIconOnly={true}
                        variant="light"
                        isLoading={isThisGenerating}
                        onPress={() => navigator.clipboard.writeText(item?.api_key_value)}
                        className="min-w-0 px-2"
                    >
                        <Icon icon="hugeicons:copy-01" width="18" height="18" color="primary" />
                    </CtyButton>
                </Tooltip>
            )}
        </div>
    );
}

function ApiKeysGridContent({
    apiKeysList,
    daysLeft,
    textCapitalize,
    handleShowDeleteConfirmationModal,
    handleViewAPIKey,
    t,
    isCreateAPiKeyLoading,
    currentItem,
    formatDate,
    onGridReady,
    isActionsDisabled,
}: ApiKeysGridContentProps) {
    const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
      useAgGridStatePersistence('api-keys-list-table');

    const gridStyle = useMemo(() => ({
        width: '100%',
        '--ag-border-color': '#e5e7eb',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
    } as React.CSSProperties), []);

    const defaultColDef = useMemo(() => ({ resizable: true }), []);
    const paginationPageSizeSelector = useMemo(() => [10, 20, 40, 50], []);

    const handleGridReady = useCallback((params: GridReadyEvent) => {
        augmentGridReady(params, (p) => {
            p?.api?.sizeColumnsToFit?.();
            onGridReady?.(p);
        });
    }, [onGridReady, augmentGridReady]);

    const columnDefs = useMemo<ColDef[]>(() => [
        {
            headerName: t('Name'),
            field: 'api_name',
            sortable: true,
            flex: 1,
            minWidth: 180,
            valueFormatter: (params) => (params.value ? textCapitalize(String(params.value)) : '—'),
        },
        {
            headerName: t('Status'),
            field: 'status',
            sortable: true,
            minWidth: 120,
            valueGetter: (params) => {
                const days = params.data ? daysLeft(params.data.expiration_date) : 0;
                return days <= 0 ? 'Revoked' : 'Active';
            },
            cellRenderer: (params: { data: any }) => (
                <StatusCellRenderer data={params.data} daysLeft={daysLeft} t={t} />
            ),
        },
        {
            headerName: t('Expiration'),
            field: 'expiration_date',
            sortable: true,
            minWidth: 140,
            valueFormatter: (params) => formatDate(params.value ?? ''),
        },
        {
            headerName: t('Created'),
            field: 'created_at',
            sortable: true,
            minWidth: 140,
            valueFormatter: (params) => formatDate(params.value ?? ''),
        },
        {
            headerName: t('Actions'),
            field: 'actions',
            sortable: false,
            minWidth: 180,
            maxWidth: 220,
            pinned: 'right',
            lockPinned: true,
            lockPosition: true,
            cellRenderer: (params: { data: any }) => (
                <ApiKeyActionsCell
                    item={params.data}
                    daysLeft={daysLeft}
                    isCreateAPiKeyLoading={isCreateAPiKeyLoading}
                    currentItem={currentItem}
                    onView={handleViewAPIKey}
                    onDelete={handleShowDeleteConfirmationModal}
                    onGenerate={(item) => handleViewAPIKey(item, true)}
                    t={t}
                    isDisabled={isActionsDisabled}
                />
            ),
        },
    ], [t, daysLeft, textCapitalize, formatDate, handleViewAPIKey, handleShowDeleteConfirmationModal, isCreateAPiKeyLoading, currentItem]);

    const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

    return (
        <div className="ag-theme-alpine mt-4 pb-4" style={gridStyle}>
            <AgGridReact
                suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
                rowData={apiKeysList ?? []}
                columnDefs={columnDefsWithSort}
                defaultColDef={defaultColDef}
                headerHeight={40}
                domLayout="autoHeight"
                onGridReady={handleGridReady}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={paginationPageSizeSelector}
                alwaysShowHorizontalScroll={true}
                suppressRowClickSelection={true}
            />
        </div>
    );
}

const ApiKeysGridContentWithErrorBoundary = ErrorBoundary(ApiKeysGridContent as unknown as React.ComponentType<Record<string, unknown>>);

const ApiKeysList = () => {
    const { t } = useTranslation();
    const hasApiKeysAccess = useHasApplicationResource('api_keys');
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const formState = params.get('state');
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [currentItem, setCurrentItem] = useState<any>();
    const [localApiKeysList, setLocalApiKeysList] = useState<any[]>([]);
    const defaultAdvisorLogo = generateCircuitryLogoSvgBlobUrl('#d4007e');

    const [deleteApiKey, { isLoading: isDeleteApiKeyLoading }] = useDeleteApiKeyMutation();
    const [createApiKey, { isLoading: isCreateAPiKeyLoading }] = useCreateApiKeyMutation();
    const { data: apiKeysList, error: isApiKeysError, isLoading: isApiKeysListLoading, isFetching: isApiKeysListFetching, refetch: refetchApiKeysList } = useGetApiKeysListQuery(undefined, { refetchOnMountOrArgChange: true });

    const handleAPIKeyDelete = async () => {
        try {
            const response = await deleteApiKey(currentItem.api_key_id).unwrap();
            setLocalApiKeysList((prevList) => prevList.filter((item) => item.api_key_id !== currentItem.api_key_id));
            CtyShowToast(checkType.success, response?.message);
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to delete API Key details')}: ${(error as Error)?.message || 'Something went wrong!'}`);
        } finally {
            setShowDeleteModal(false);
        }
    };

    useEffect(() => {
        if (apiKeysList) {
            setLocalApiKeysList(apiKeysList);
        }
    }, [apiKeysList]);

    const handleCreateConnection = () => {
        navigate(`/settings/apikey?state=${FormState.CREATE}`);
    };

    const onSubmit = async (data: any) => {
        try {
            const payload = {
                api_name: data.api_name,
                expiration_days: +data.expiration_days,
                domain_name: data.domain_name,
            };
            const response = await createApiKey(payload).unwrap();
            navigate('/settings/apikey');
            CtyShowToast(checkType.success, response?.message);
            refetchApiKeysList();
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to update ApiKey details')}: ${(error as Error)?.message || 'Something went wrong!'}`);
        }
    };

    const calculateExpirationDays = (createdDate: string, expirationDate: string) => {
        const diffInMilliseconds = new Date(expirationDate).getTime() - new Date(createdDate).getTime();
        return Math.round(diffInMilliseconds / (1000 * 60 * 60 * 24));
    };

    const handleExpiredAPIKey = (item: any) => {
        setCurrentItem(item);
        const expirationDays = calculateExpirationDays(item?.created_at, item?.expiration_date);
        onSubmit({ api_name: item.api_name, expiration_days: expirationDays, domain_name: item.domain_name });
    };

    const handleNonExpiredAPIKey = (item: any) => {
        navigate(`/settings/apikey?state=${FormState.UPDATE}`, { state: { apiKeyDetails: item } });
    };

    const handleViewAPIKey = (item: any, isApiKeyExpired = false) => {
        isApiKeyExpired ? handleExpiredAPIKey(item) : handleNonExpiredAPIKey(item);
    };

    const handleShowDeleteConfirmationModal = (item: any) => {
        setShowDeleteModal(true);
        setCurrentItem(item);
    };

    function daysLeft(targetDate: string) {
        const currentDate = new Date();
        const target = new Date(targetDate);
        const timeDifference = target.getTime() - currentDate.getTime();
        const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
        return daysRemaining > 0 ? daysRemaining : 0;
    }

    const formatDate = useCallback((dateStr: string) => {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return dateStr;
        }
    }, []);

    const isShowApiKeyListEnabled = formState === null;
    const isLoadingApiKeysList = isApiKeysListFetching || isApiKeysListLoading;
    const isShowApiKeyFormEnabled = formState === FormState.CREATE || formState === FormState.UPDATE;
    const isCreateNewApiKeyEnabled = (localApiKeysList || []).length === 0 && formState === null && !isLoadingApiKeysList && !isApiKeysError;

    function createAPIKeyUI() {
        return (
            <div className=" flex-1 justify-center items-center mt-3">
                <div className="flex h-full flex-1 flex-col gap-3 items-center justify-center border-2 border-dotted border-default-300 p-6 rounded-lg ">
                    <div className="bg-content1 shadow-custom w-[56px] h-[56px] br-[50%] rounded-full border border-default-300 flex items-center justify-center">
                        <Image alt="advisor-logo" src={defaultAdvisorLogo} radius="sm" height={25} width={25} />
                    </div>
                    <p>Create your first API key</p>
                    <CtyButton variant="light" size="sm" onPress={handleCreateConnection} className="border-1 border-default-300" isDisabled={!hasApiKeysAccess}>
                        Create
                    </CtyButton>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full pt-2">
            {isShowApiKeyListEnabled && (
                <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
                    <div className="flex flex-col gap-1">
                        <h1 className="font-poppins text-2xl font-semibold leading-8 text-foreground">{t('API Access Keys')}</h1>
                        <p className="font-poppins text-sm font-normal leading-5 text-default-600">{t('Manage API keys for programmatic access')}</p>
                    </div>
                    <CtyButton
                        color="primary"
                        size="md"
                        className="shrink-0 font-semibold"
                        onPress={handleCreateConnection}
                        startContent={<Icon icon="fa6-solid:plus" width="16" height="16" />}
                        isDisabled={!hasApiKeysAccess}
                    >
                        {t('Create New Key')}
                    </CtyButton>
                </header>
            )}
            {isShowApiKeyFormEnabled && <ApiKeyConfigurationWithErrorBoundary refetchApiKeysList={refetchApiKeysList} />}
            {isCreateNewApiKeyEnabled && createAPIKeyUI()}
            {isShowApiKeyListEnabled && isLoadingApiKeysList && (localApiKeysList?.length ?? 0) === 0 && (
                <div className="flex justify-center items-center min-h-[200px] mt-4">
                    <Spinner size="lg" />
                </div>
            )}
            {isShowApiKeyListEnabled && isApiKeysError && (
                <div className="text-danger text-sm py-4 mt-4">{t('Failed to load API keys')}</div>
            )}
            {isShowApiKeyListEnabled && (localApiKeysList?.length ?? 0) > 0 && (
                <ApiKeysGridContentWithErrorBoundary
                    apiKeysList={localApiKeysList}
                    daysLeft={daysLeft}
                    textCapitalize={textCapitalize}
                    handleShowDeleteConfirmationModal={handleShowDeleteConfirmationModal}
                    handleViewAPIKey={handleViewAPIKey}
                    t={t}
                    isCreateAPiKeyLoading={isCreateAPiKeyLoading}
                    currentItem={currentItem}
                    formatDate={formatDate}
                    isActionsDisabled={!hasApiKeysAccess}
                />
            )}
            {showDeleteModal && <DeleteConfirmationModalWithErrorBoundary
                isOpen={showDeleteModal}
                onOpenChange={(open: boolean) => { if (!open) setShowDeleteModal(false); }}
                onConfirm={handleAPIKeyDelete}
                isConfirmLoading={isDeleteApiKeyLoading}
                requireTypedConfirmation={false}
                title={t('Delete Confirmation')}
                body={
                    <p className="text-sm font-semibold">
                        {t('Please confirm that')}{' '}
                        <span className="text-danger">{currentItem.api_name + ' API Key'}</span>{' '}
                        {t('is no longer needed.')}
                    </p>
                }
            />}

        </div>
    );
};

export default ApiKeysList;